import type {
  MachineAttachmentDto,
  MachineCompatibilityDto,
  MachineDto
} from '@api/machines'
import { GENERAL_MACHINE_TYPES } from '@common/utils'
import { MolsonTypography } from '@components/reusable'
import { molsonUseTheme } from '@theme/customMuiExports'
import { useTranslation } from 'react-i18next'
import { useFetchCompatibleMachinesBySpecIdAndTemplateId } from '@/shared-queries/machines'
import { ModalGridLoader } from '../modal-grid-loader/ModalGridLoader'
import { StyledGrid } from './StyledGrid'

export interface Equipments {
  attachments: MachineAttachmentDto[]
  machines: (MachineDto | MachineCompatibilityDto)[]
}

export const SelectEquipmentsStepTemporaryCustomers = ({
  isMachine,
  selectedEquipments,
  setSelectedEquipments,
  translationKeyPrefix,
  specId,
  templateId
}: {
  isMachine: boolean
  selectedEquipments: Equipments
  setSelectedEquipments: React.Dispatch<React.SetStateAction<Equipments>>
  translationKeyPrefix: string
  specId?: string
  templateId?: string
}) => {
  const { t } = useTranslation('', {
    keyPrefix: translationKeyPrefix
  })
  const theme = molsonUseTheme()

  const {
    data: compatibleItemsData = [],
    isLoading: isCompatibleItemsLoading
  } = useFetchCompatibleMachinesBySpecIdAndTemplateId({
    specId: specId ?? '',
    templateId: templateId ?? ''
  })

  const getCompatibilityText = (compatibility: number) => {
    switch (compatibility) {
      case 1:
        return (
          <MolsonTypography color={theme.palette.primary.success} fontSize={14}>
            Compatible
          </MolsonTypography>
        )
      case 2:
        return (
          <MolsonTypography
            color={theme.palette.secondary.warning}
            fontSize={14}
          >
            Partially Compatible
          </MolsonTypography>
        )
      case 3:
        return (
          <MolsonTypography color={theme.palette.primary.error} fontSize={14}>
            Not Compatible
          </MolsonTypography>
        )

      default:
        return 'Unknown'
    }
  }

  const equipmentKey = isMachine
    ? GENERAL_MACHINE_TYPES.MACHINE
    : GENERAL_MACHINE_TYPES.ATTACHMENT

  const dataSource = compatibleItemsData?.filter(
    item =>
      item?.machineType === equipmentKey &&
      (item?.compatibility === 1 || item?.compatibility === 2)
  )

  return isCompatibleItemsLoading ? (
    <ModalGridLoader />
  ) : (
    <StyledGrid
      columnsProps={[
        {
          caption: t('model') ?? '',
          dataField: 'modelName'
        },
        {
          caption: t('category') ?? '',
          dataField: 'category'
        },
        {
          caption: t('manufactureYear') ?? '',
          dataField: 'manufactureYear'
        },
        {
          caption: t('stockType') ?? '',
          dataField: 'stockType'
        },
        {
          caption: 'Compatibility',
          dataField: 'compatibility',
          alignment: 'left',
          cellRender: (e: { data: { compatibility: number } }) =>
            getCompatibilityText(e.data.compatibility)
        }
      ]}
      headerFilterProps={{
        visible: true
      }}
      onCellPrepared={e =>
        e.rowType === 'data' &&
        e.cellElement.style.setProperty('cursor', 'pointer', 'important')
      }
      onContentReady={e => {
        // For single selection, select the first item if it exists
        const selectedItems = isMachine
          ? selectedEquipments.machines
          : selectedEquipments.attachments
        if (selectedItems.length > 0) {
          e.component.selectRows([selectedItems[0]], false)
        }
      }}
      onRowClick={e => {
        const grid = e.component
        const rowKey = grid.getKeyByRowIndex(e.rowIndex)
        // For single selection, always clear previous selection and select the clicked row
        grid.clearSelection()
        grid.selectRows([rowKey], false)
      }}
      onSelectionChanged={selectedRows => {
        const { selectedRowsData } = selectedRows
        setSelectedEquipments(prevState => ({
          ...prevState,
          machines: isMachine ? selectedRowsData : prevState.machines,
          attachments: isMachine ? prevState.attachments : selectedRowsData
        }))
      }}
      selection={{
        mode: 'single'
      }}
      searchPanelProps={{
        highlightSearchText: true,
        placeholder: t('searchPlaceholder') ?? '',
        visible: true
      }}
      dataSource={dataSource}
    />
  )
}
